import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

import clsx from 'clsx';
import { Skeleton } from '@material-ui/lab';
import { makeStyles } from '@material-ui/styles';
import { Typography, Switch } from '@material-ui/core';

import { InvoiceEmpty } from '../../../../constants';
import { addCommasToNumber, hasPermission } from '../../../Utils/helper';

const useStyles = makeStyles(() => ({
  background__style: {
    background: '#FFFFFF',
    borderRadius: '20px',
    height: '25rem',
  },
  space__between: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  empty__screen: {
    padding: '2rem .5rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
  },
  empty__icon: {
    height: '115px',
    width: '150px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '& svg': {
      width: '80%',
    },
  },
  empty__text: {
    color: '#ACC1D4',
    fontSize: '14px',
    textAlign: 'center',
    marginTop: '2rem',
    width: '95%',
  },
  header: {
    padding: '1rem',
  },
  heading: {
    fontWeight: '600',
    fontSize: '14px',
    lineHeight: '19px',
    color: '#6d7d8b',
    marginBottom: '.3rem',
  },
  amount: {
    fontWeight: 'bold',
    fontSize: '20px',
    lineHeight: '35px',
    color: '#29264a',
  },
  toggle__label: {
    fontSize: '14px',
    lineHeight: '19px',
    fontWeight: '600',
    color: 'rgba(109, 125, 139, 0.5)',
  },
  active__label: {
    color: 'var(--color-primary)',
  },
  toggle__switch: {
    width: '36px',
    height: '16px',
    borderRadius: '30px',
    padding: '0',
    margin: '0 .5rem',
    '& .MuiSwitch-switchBase': {
      padding: '0',
      color: '#fff',
      height: '100%',
    },
    '& .MuiSwitch-colorSecondary.Mui-checked + .MuiSwitch-track': {
      backgroundColor: 'var(--color-primary)',
      opacity: '1',
    },
    '& .MuiSwitch-track': {
      backgroundColor: 'var(--color-primary)',
      opacity: '1',
    },
    '& .MuiSwitch-thumb': {
      width: '12px',
      height: '12px',
      marginLeft: '2px',
    },
  },
  content: {
    padding: '0 1rem',
    height: 'calc(100% - 110px)',
    overflowY: 'auto',
  },
  invoice__listitem: {
    background: '#f6f6f8',
    borderRadius: '15px',
    padding: '1rem',
    marginBottom: '1rem',
    cursor: 'pointer',
  },
  pt__1: {
    paddingTop: '.5rem',
    alignItems: 'flex-end',
  },
  customer__name: {
    fontWeight: 'bold',
    fontSize: '16px',
    lineHeight: '21px',
    color: '#29264a',
  },
  repair__order__num: {
    fontSize: '12px',
    lineHeight: '14px',
    fontWeight: '600',
    color: '#6d7d8b',
  },
  total__price: {
    fontWeight: '600',
    fontSize: '18px',
    lineHeight: '24px',
    color: 'var(--color-error)',
  },
  car__details: {
    fontSize: '13px',
    lineHeight: '17px',
    color: '#6d7d8b',
    fontWeight: '600',
    '& .dot': {
      color: '#c4c4c4',
      padding: '0 .5rem',
    },
    '& .car__model': {
      color: '#29264a',
    },
  },
}));

export default function Invoices({ currency }) {
  const classes = useStyles();
  const [toggle, setToggle] = useState(false);
  const [invliceList, setInvliceList] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const invoiceListData = useSelector((state) => state.invoice?.invoices || []);
  const getInvoicesLoading = useSelector(
    (state) => state.invoice?.getInvoicesLoading || false,
  );

  useEffect(() => {
    if (invoiceListData?.length) {
      let invoiceTotal = 0;
      let filterdList = invoiceListData?.filter((inv) => {
        if (
          inv?.estimateId &&
          Math.round((+inv?.remainingAmount || 0) * 100) / 100 > 0
        ) {
          // dont show quickcheckout invoices here
          invoiceTotal += +inv.remainingAmount || 0;
          return inv;
        }
      });

      // sort list by latest invoice
      filterdList = filterdList.sort(
        (a, b) => new Date(b?.updatedAt) - new Date(a?.updatedAt),
      );

      setInvliceList(filterdList);
      setTotalAmount(invoiceTotal);
    }
  }, [invoiceListData]);

  const handleChangeToggle = (e) => {
    const { checked } = e.target;
    if (checked) {
      // largest sort by amount
      invliceList.sort(
        (a, b) => Number(b?.remainingAmount) - Number(a?.remainingAmount),
      );
    } else {
      // latest sort by date
      invliceList.sort(
        (a, b) => new Date(b?.updatedAt) - new Date(a?.updatedAt),
      );
    }

    setInvliceList(invliceList);
    setToggle(checked);
  };

  const history = useHistory();

  const redirecttoPayment = (id) => {
    const permission = hasPermission('repairOrder', 'Payment & Invoice');
    if ((permission === 'owner' || permission) && id) {
      history.push(`new-repairorder/${id}?tab=4`);
    }
  };
  return (
    <>
      <div className={classes.background__style}>
        <div className={clsx(classes.header, classes.space__between)}>
          <div>
            <Typography className={classes.heading}>
              {getInvoicesLoading ? <Skeleton width={75} /> : 'Unpaid Invoices'}
            </Typography>
            {getInvoicesLoading ? (
              <Typography className={classes.amount}>
                <Skeleton width={25} />
              </Typography>
            ) : (
              <Typography className={classes.amount}>
                {currency}

                {addCommasToNumber(
                  Math.round(totalAmount * 100) / 100 || '0.00',
                )}
              </Typography>
            )}
          </div>
          <div className='d-flex align-center'>
            <Typography
              className={clsx(
                classes.toggle__label,
                !toggle && classes.active__label,
              )}
            >
              Latest
            </Typography>
            <Switch
              checked={toggle}
              onChange={handleChangeToggle}
              name='toggle'
              className={classes.toggle__switch}
              inputProps={{
                'aria-label': 'secondary checkbox',
              }}
              disabled={getInvoicesLoading}
            />
            <Typography
              className={clsx(
                classes.toggle__label,
                toggle && classes.active__label,
              )}
            >
              Largest
            </Typography>
          </div>
        </div>
        <div className={classes.content}>
          {invliceList?.length < 1 && !getInvoicesLoading ? (
            <div className={classes.empty__screen}>
              <div className={classes.empty__icon}>
                <InvoiceEmpty />
              </div>
              <Typography className={classes.empty__text}>
                Looks like you don’t have any unpaid invoices. Once you have
                some, they will appear here.
              </Typography>
            </div>
          ) : (
            <div className={classes.unpaid__invoices}>
              {getInvoicesLoading
                ? [0, 1, 2].map((i) => {
                    return (
                      <div className={classes.invoice__listitem} key={i++}>
                        <div className={classes.space__between}>
                          <Typography className={classes.customer__name}>
                            <Skeleton width={75} />
                          </Typography>
                          <Typography className={classes.repair__order__num}>
                            <Skeleton width={45} />
                          </Typography>
                        </div>
                        <div
                          className={clsx(
                            classes.space__between,
                            classes.pt__1,
                          )}
                        >
                          <Typography className={classes.car__details}>
                            <Skeleton width={95} />
                          </Typography>
                          <Typography className={classes.total__price}>
                            <Skeleton width={75} />
                          </Typography>
                        </div>
                      </div>
                    );
                  })
                : invliceList?.map((inv) => {
                    return (
                      <div
                        className={classes.invoice__listitem}
                        onClick={() => redirecttoPayment(inv?.repairOrder?.id)}
                      >
                        <div className={classes.space__between}>
                          <Typography className={classes.customer__name}>
                            {' '}
                            {inv?.repairOrder?.customer?.name || ''}
                          </Typography>
                          <Typography className={classes.repair__order__num}>
                            #{inv?.repairOrder?.repairOrderId}
                          </Typography>
                        </div>
                        <div
                          className={clsx(
                            classes.space__between,
                            classes.pt__1,
                          )}
                        >
                          <Typography className={classes.car__details}>
                            {inv?.repairOrder?.vehicle?.vehicleModel || ''}
                            <span className='dot'>•</span>
                            <span className='car__model'>
                              {inv?.repairOrder?.vehicle?.licensePlate || ''}
                            </span>
                          </Typography>
                          <Typography className={classes.total__price}>
                            {currency}
                            {addCommasToNumber(
                              Math.round(
                                ((+inv?.remainingAmount || 0) < 0.01
                                  ? 0
                                  : +inv?.remainingAmount || 0) * 100,
                              ) / 100 || '0.00',
                            )}
                          </Typography>
                        </div>
                      </div>
                    );
                  })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
